import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { map, of } from 'rxjs';
import { IdRequest } from '../../../../../backend/src/core/request-response';
import { ResourceName } from '../../../../../backend/src/core/resource-name';
import { StripeGate } from '../../../../../backend/src/core/stripe/stripe.gate';
import { environment } from '../../../environments/environment';
import { LambdaService } from '../../shared/core/lambda.service';
import { WorkspaceHolderService } from '../workspace/workspace.holder.service';
import { InvoiceStripe } from './invoice.stripe';
import { StripeProductStatus, StripeProductType } from './product.stripe';
import { SubscriptionStripe } from './subscription.stripe';

@Injectable({
  providedIn: 'root',
})
export class StripeService {
  constructor(
    private lambdaService: LambdaService,
    private workspaceHolderService: WorkspaceHolderService
  ) {}

  private _cachedInvoiceList?: InvoiceStripe[];

  availableMemberCount() {
    if (
      this.workspaceHolderService.paidStatus()?.type === StripeProductType.premium &&
      this.workspaceHolderService.paidStatus()?.status === StripeProductStatus.trial
    ) {
      return 20;
    }
    return this.getSubscription()?.quantity ?? 0;
  }

  availableCallCount() {
    const productId = this.getSubscription()?.plan.product;

    const callLimitation =
      environment.stripe.productList.find((product) => product.id === productId)?.callLimitation ?? 0;

    const totalCall = callLimitation * this.availableMemberCount();

    return totalCall;
  }

  getInvoiceList() {
    return this._cachedInvoiceList ?? [];
  }

  getActiveInvoiceList() {
    const resultList: InvoiceStripe[] = [];

    for (const invoice of this._cachedInvoiceList ?? []) {
      let isActive = false;

      invoice.lines.data.forEach((line) => {
        const periodStart = DateTime.fromSeconds(line.period.start);
        const periodEnd = DateTime.fromSeconds(line.period.end);

        const now = DateTime.local();

        if (periodStart <= now && now <= periodEnd) {
          isActive = true;
        }
      });

      if (isActive) {
        resultList.push(invoice);
      }
    }

    return resultList;
  }

  private _cachedSubscription?: SubscriptionStripe;

  getSubscription() {
    return this._cachedSubscription;
  }

  setSubscription(subscription?: SubscriptionStripe) {
    this._cachedSubscription = subscription;
  }

  getSubscription$(id: string) {
    if (this._cachedSubscription) {
      return of(this._cachedSubscription);
    }

    const request: IdRequest = { id };
    return this.lambdaService.run$(ResourceName.stripe, StripeGate.actions.getSubscription, request).pipe(
      map((response) => {
        this._cachedSubscription = response;
        return response;
      })
    );
  }

  getInvoiceList$() {
    if (!this._cachedSubscription?.id) {
      return of([]);
    }

    if (this._cachedInvoiceList) {
      return of(this._cachedInvoiceList);
    }

    const request = { subscriptionId: this._cachedSubscription?.id ?? '' };
    return this.lambdaService.run$(ResourceName.stripe, StripeGate.actions.getInvoiceList, request).pipe(
      map((response) => {
        (response.data as InvoiceStripe[]).sort((a, b) => {
          return b.created * 1000 - a.created * 1000;
        });

        this._cachedInvoiceList = response.data;

        return response.data;
      })
    );
  }

  getCustomerPotalUrl$() {
    if (!this._cachedSubscription?.customer) {
      return of('');
    }

    return this.lambdaService
      .run$(ResourceName.stripe, StripeGate.actions.getCustomerPotalUrl, {
        customerId: this._cachedSubscription?.customer ?? '',
      })
      .pipe(
        map((response) => {
          return response?.url;
        })
      );
  }
}
